import { removeCurrentYear } from '@/utils/date/removeCurrentYear'
import { format } from '@/utils/date-fns/format'
import { useOnErrorAuto } from '@/utils/hooks/error-handling'
import { useConfirm } from '@/utils/hooks/useConfirm'
import { useMutation } from '@apollo/client/react/hooks/useMutation'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Button } from '@/components/ui/button'
import { useCallback } from 'react'
import { UserProfileLink } from '@/modules/users/UserProfile/UserProfileLink'
import UserThumbOrInitials from '@/modules/users/UserThumbOrInitials'
import { DELETE_ABSENCE_COMMENTS_MUTATION } from '../mutations'
import type { AbsenceComment } from '../types.graphql'

interface Props {
  id: string
  user: AbsenceComment['user']
  createdAt: string
  showMenu: boolean
  canDelete: boolean
  canEdit: boolean
  editing: boolean
  onChangeEditing(editing: boolean): void
}

export function AbsenceCommentCardHeader({
  id,
  user,
  createdAt,
  showMenu,
  canDelete,
  canEdit,
  editing,
  onChangeEditing,
}: Props) {
  const confirm = useConfirm()
  const onErrorAuto = useOnErrorAuto()
  const translations = useTranslate({
    writtenWhen: ['absence.comment.written-when', { when: '' }],
    deleteComment: 'absence.comment.delete-comment',
    deleteCommentTitle: 'absence.comment.delete-comment-title',

    edit: 'common.edit',
    cancelEdit: 'common.cancel-changes',
    delete: 'common.delete-alt',
  })

  const [deleteCommentMutation, { loading }] = useMutation(
    DELETE_ABSENCE_COMMENTS_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: ['AbsenceComments', 'AbsenceDetails'],
      onError: onErrorAuto(),
    }
  )

  const handleDeleteComment = useCallback(async () => {
    if (!canDelete) return

    const { data: answer } = await confirm(
      translations.deleteComment,
      translations.deleteCommentTitle
    )
    if (!answer) return

    onChangeEditing(false)

    void deleteCommentMutation({
      variables: {
        id,
      },
    })
  }, [
    canDelete,
    confirm,
    deleteCommentMutation,
    id,
    onChangeEditing,
    translations.deleteComment,
    translations.deleteCommentTitle,
  ])

  return (
    <header className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <UserThumbOrInitials user={user} size={40} fontSize="0.75em" />

        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-stone-900">
            <UserProfileLink user={user} />
          </div>
          <p className="mt-0.5 text-xs text-stone-500">
            {translations.writtenWhen({
              when: removeCurrentYear(format(new Date(createdAt), 'PPP HH:mm')),
            })}
          </p>
        </div>
      </div>

      {showMenu && (
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChangeEditing(!editing)}
              className="h-7 rounded-md border-stone-300 px-2.5 text-xs text-stone-700 hover:bg-stone-100"
            >
              {editing ? translations.cancelEdit : translations.edit}
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="outline"
              disabled={loading}
              onClick={handleDeleteComment}
              className="h-7 rounded-md border-rose-300 px-2.5 text-xs text-rose-700 hover:bg-rose-50 hover:text-rose-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {translations.delete}
            </Button>
          )}
        </div>
      )}
    </header>
  )
}
